"use client";
import React, { useRef } from "react";
import {
  motion,
  MotionValue,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue,
  useMotionTemplate,
} from "motion/react";
import Image from "next/image";
import { ProjectType } from "@/components/utils/types";
import { useTheme } from "next-themes";

interface ShowcaseImageProps {
  project: ProjectType;
  scaleImg: MotionValue<number>;
  opacityImg: MotionValue<number>;
}

export const ShowcaseImage = ({
  project,
  scaleImg,
  opacityImg,
}: ShowcaseImageProps) => {
  const { theme } = useTheme();
  const sectionRef = useRef<HTMLDivElement>(null);

  // Parallax scroll effect for the image inside the scaled container
  const { scrollYProgress: sectionScroll } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const yImgRaw = useTransform(sectionScroll, [0, 1], ["-10%", "10%"]);
  const yImg = useSpring(yImgRaw, {
    stiffness: 80,
    damping: 25,
    restDelta: 0.001,
  });

  // Mouse glare/spotlight coordinates
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const glareOpacityRaw = useMotionValue(0);
  const glareOpacity = useSpring(glareOpacityRaw, {
    stiffness: 150,
    damping: 25,
  });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set(e.clientX - rect.left);
    mouseY.set(e.clientY - rect.top);
    glareOpacityRaw.set(1);
  };

  const handleMouseLeave = () => {
    glareOpacityRaw.set(0);
  };

  const spotlightColor =
    theme === "dark"
      ? "rgba(255, 255, 255, 0.08)"
      : "rgba(255, 255, 255, 0.25)";
  const glareBackground = useMotionTemplate`radial-gradient(600px circle at ${mouseX}px ${mouseY}px, ${spotlightColor}, transparent 80%)`;

  // Text Entrance Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.15,
      },
    },
  } as const;

  const textVariants = {
    hidden: { opacity: 0, y: 30, filter: "blur(4px)" },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: {
        type: "spring",
        stiffness: 70,
        damping: 16,
      },
    },
  } as const;

  return (
    <section
      ref={sectionRef}
      className="relative w-full px-2 sm:px-4 md:px-16 lg:px-32 py-16 sm:py-20 md:py-32 z-20"
    >
      <motion.div
        style={{ scale: scaleImg, opacity: opacityImg }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="relative flex justify-center items-center w-full aspect-4/3 sm:aspect-video md:aspect-977/460 rounded-[20px] sm:rounded-[30px] md:rounded-[60px] overflow-hidden shadow-2xl border border-black/5 dark:border-white/10 cursor-pointer"
      >
        {/* Dynamic Spotlight Glare Overlay */}
        <motion.div
          className="absolute inset-0 pointer-events-none z-30 transition-opacity duration-300"
          style={{ opacity: glareOpacity, background: glareBackground }}
        />

        {/* Text content with staggered slide-up & fade-in */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="absolute w-[92%] sm:w-[90%] md:w-2xl lg:w-3xl bottom-4 sm:bottom-8 md:bottom-12 left-3 sm:left-4 md:left-6 lg:left-8 xl:left-12 flex flex-col gap-1 sm:gap-2 z-20"
        >
          <motion.div
            variants={textVariants}
            className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl xl:text-7xl text-white font-recoleta font-bold tracking-wide"
          >
            About
          </motion.div>
          <motion.div
            variants={textVariants}
            className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-white/90 font-outfit tracking-wide line-clamp-2 sm:line-clamp-3 md:line-clamp-4"
          >
            {project.description}
          </motion.div>
        </motion.div>

        {/* Gradients for text contrast */}
        <div className="absolute inset-0 bg-linear-to-t from-black/90 via-black/50 to-transparent sm:bg-linear-to-tr sm:from-black/80 sm:via-black/40 sm:to-transparent z-10" />
        <div className="absolute inset-0 bg-linear-to-t from-black/60 to-transparent sm:bg-linear-to-tr sm:from-black/80 sm:via-black/10 sm:to-transparent z-10" />

        {/* Parallax Image Container */}
        <motion.div
          style={{ y: yImg, scale: 1.2 }}
          className="absolute inset-0 w-full h-full"
        >
          <Image
            src={
              theme === "dark" && project?.mainPagePoster?.[1]
                ? project?.mainPagePoster?.[1].image
                : project?.mainPagePoster?.[0].image!
            }
            alt={`${project.name} Showcase`}
            fill
            className="object-cover"
            priority
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 80vw"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};
